import type {
  Block,
  Column,
  DocumentSection,
  EmailDocument,
  Group,
  Row,
} from "./types";
import { isGroup, isRow } from "./sections";

export type RowLocation =
  | { parent: 'document'; sectionIndex: number }
  | { parent: 'group'; groupId: string; rowIndex: number };

export type LocateRowResult = {
  row: Row;
  location: RowLocation;
};

export function locateRow(
  sections: DocumentSection[],
  rowId: string,
): LocateRowResult | undefined {
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];
    if (isRow(section) && section.id === rowId) {
      return { row: section, location: { parent: 'document', sectionIndex } };
    }
    if (isGroup(section)) {
      const rowIndex = section.rows.findIndex(row => row.id === rowId);
      if (rowIndex >= 0) {
        return {
          row: section.rows[rowIndex],
          location: { parent: 'group', groupId: section.id, rowIndex },
        };
      }
    }
  }
  return undefined;
}

export function findGroup(
  sections: DocumentSection[],
  groupId: string,
): Group | undefined {
  const section = sections.find(s => s.id === groupId);
  return section && isGroup(section) ? section : undefined;
}

export function findRow(doc: EmailDocument, rowId: string): Row | undefined {
  return locateRow(doc.sections, rowId)?.row;
}

export function findColumn(
  doc: EmailDocument,
  rowId: string,
  columnId: string,
): Column | undefined {
  return findRow(doc, rowId)?.columns.find(column => column.id === columnId);
}

export function findColumnInSections(
  sections: DocumentSection[],
  rowId: string,
  columnId: string,
): Column | undefined {
  const located = locateRow(sections, rowId);
  return located?.row.columns.find(column => column.id === columnId);
}

export function findBlock(
  doc: EmailDocument,
  rowId: string,
  columnId: string,
  blockId: string,
): Block | undefined {
  return findColumn(doc, rowId, columnId)?.blocks.find(
    block => block.id === blockId,
  );
}

export type BlockLocation = {
  rowId: string;
  columnId: string;
  blockId: string;
};

export function locateBlockById(
  doc: EmailDocument,
  blockId: string,
): BlockLocation | undefined {
  for (const section of doc.sections) {
    const rows = isRow(section) ? [section] : section.rows;
    for (const row of rows) {
      for (const column of row.columns) {
        const block = column.blocks.find(candidate => candidate.id === blockId);
        if (block) {
          return {
            rowId: row.id,
            columnId: column.id,
            blockId: block.id,
          };
        }
      }
    }
  }

  return undefined;
}
